/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@flow
@format
*/

import React from 'react';
import styled from 'styled-components';
import { withAppManager, LoadingSpinner } from '@ziynx/components/utilities';
import { Heading, SubHeading, Body } from '@ziynx/components/typography';
import { Popover } from '@ziynx/components/popovers';
import { Constants } from '@ziynx/components/layout';
import Colors from '@ziynx/theme/colors';
import Icon from '@ziynx/components/icons';
import Image from '@ziynx/components/images';
import Link from '@ziynx/components/links';
import type { RightActionType, ImagePreviewType } from '@ziynx/flow-types/globals';
import type { InlineLink as InlineLinkType } from '@ziynx/flow-types/links';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import * as KrispyTokens from '@krispy-tokens/blue';

import CardBase from './CardBase';

export type Props = $ReadOnly<{|
  /**
   * To determine fixed dimensions for the card
   */
  fixedWidth?: boolean,
  /**
   * If fixed is true, size is set
   */
  size?: 'extraSmall' | 'small' | 'medium' | 'large',
  /**
   * Video thumbnail preview config object
   */
  image?: ImagePreviewType,
  /**
   * Right action type object
   */
  rightAction?: RightActionType,
  /**
   * TextLink props
   */
  link?: InlineLinkType,
  /**
   * Title for the card
   */
  title?: string,
  /**
 * Subtitle for the card
 */
  subtitle?: string,
  /**
 * Color for the subtitle
 */
  subtitleColor: string,
  /**
 * Body for the card
 */
  body?: string,
  /**
 * Text for the notification, if any
 */
  notificationText?: string,
  /**
 * Depicts when the image is in loading state
 */
  isLoading?: boolean,
  /**
 * Archived prop
 */
  archived?: boolean,
  /** Shows menu in loading state - used for custom uploading states
   */
  showMenuOnLoad?: boolean
|}>;

const StyledCardBase = styled(CardBase)`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${({ isMobile }) => (isMobile ? '16px' : '24px')};
  min-width: 288px;
  max-width: 688px;
  box-sizing: border-box;
`;

const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 50px;
  height: 50px;
  ${({ empty }) => empty
    && `border-radius: ${Constants.borderRadiusSmall}px;
    border: 2px dashed ${Colors.GrayLight};`}
  box-sizing: border-box;
`;

const TextWrapper = styled.span`
  width: calc(100% - 100px);
  padding: 0px ${({ isMobile }) => (isMobile
    ? `${KrispyTokens.KrispySpacingElementsHdXlarge}px`
    : `${KrispyTokens.KrispySpacingElementsMdSmall}px`)};
  box-sizing: border-box;
`;

const StyledHeading = styled(Heading)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledSubHeading = styled(SubHeading)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LoadingSpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RightCTAWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  box-sizing: border-box;
`;

const StyledLink = styled(Link)`
  text-align: center;
  justify-content: center;
  display: flex;
  align-items: center;
  width: 44px;
  height: 44px;
`;

const BodyWrapper = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    box-sizing: border-box;
    min-width: 80px;
`;

const StyledBody = styled(Body)`
  padding-top: ${KrispyTokens.KrispySpacingElementsHdSmall}px;
  ${({ subtitle, isMobile }) => subtitle
  && `
  max-height: ${isMobile ? '16px' : '22px'};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;`
}
`;

function _getWidth(widthProps: $ReadOnly<{
  fixedWidth?: boolean,
  size?: 'extraSmall' | 'small' | 'medium' | 'large',
}>) {
  const {
    fixedWidth, size,
  } = widthProps || {};

  if (!fixedWidth) return '100%';

  const width = '';
  switch (size) {
  case 'large':
    return '688px';
  case 'medium':
    return '520px';
  case 'small':
    return '382px';
  case 'extraSmall':
    return '288px';
  default:
    return width;
  }
}

const CardVideoPreview = (props: ManagedProps<Props>): React$Node => {
  const {
    viewport,
    fixedWidth,
    size,
    title,
    image,
    link,
    isLoading,
    rightAction,
    subtitle,
    subtitleColor,
    body,
    archived,
    notificationText,
    showMenuOnLoad,
    ...rest
  } = props;

  const { imageSource, imageAltText, ...restImageProps } = image || {};

  const isEmpty = !imageSource;

  const isMobile = viewport === 'mobile' || viewport === 'mobileSmall';

  const isFixedSmall = fixedWidth && (size === ('small' || 'extraSmall'));

  const CARD_WIDTH = _getWidth(props);

  const CARD_HEIGHT = (isMobile || isFixedSmall) ? '82px' : '98px';

  const _renderImagePreview = (): React$Node => (
    <ImageWrapper empty={isEmpty}>
      {isLoading ? (
        <LoadingSpinnerWrapper>
          <LoadingSpinner color={Colors.GrayLight} />
        </LoadingSpinnerWrapper>
      ) : isEmpty ? (
        <Icon mini color={Colors.GrayLight} name="play-circle" />
      ) : (
        <Image
          {...restImageProps}
          isBackground
          aspectRatio="1:1"
          layoutConfig={{ borderRadius: `${Constants.borderRadius}px` }} // 4px
          src={imageSource}
          alt={imageAltText || 'Preview image'}
        />
      )}
    </ImageWrapper>

  );

  const _renderTextSection = (): React$Node => {
    const { onClick, children, disabled } = link || {};
    const onClickFn = disabled || !onClick ? () => {} : (e) => onClick(e);
    if (isLoading) {
      return (
        <TextWrapper isMobile={isMobile || isFixedSmall}>
          <Link variant="text" large>Loading...</Link>
        </TextWrapper>
      );
    }
    if (isEmpty) {
      return (
        <TextWrapper isMobile={isMobile || isFixedSmall}>
          <Link variant="text" large onClick={onClickFn}>{children || 'Add video'}</Link>
        </TextWrapper>
      );
    }

    return (
      <TextWrapper isMobile={isMobile || isFixedSmall}>
        {(isMobile || isFixedSmall)
          ? (
            <StyledSubHeading medium color={Colors.GrayVeryDark}>
              {title}
            </StyledSubHeading>
          )
          : (
            <StyledHeading small color={Colors.GrayVeryDark}>
              {title}
            </StyledHeading>
          )}
        {subtitle && (
          <StyledBody
            medium={!isMobile && !isFixedSmall}
            extraSmall={isMobile || isFixedSmall}
            color={subtitleColor || Colors.GrayDark}
            subtitle
            isMobileSmall={isMobile}
          >
            {subtitle}
          </StyledBody>
        )}
        <BodyWrapper>
          <StyledBody
            medium={!isMobile && !isFixedSmall}
            extraSmall={isMobile || isFixedSmall}
            color={Colors.GrayDark}
          >
            {body}
          </StyledBody>
          {archived && isMobile && <StyledBody extraSmall color={Colors.GrayDark}>Archived</StyledBody>}
        </BodyWrapper>
      </TextWrapper>
    );
  };

  const _renderRightCTA = (): React$Node => {
    const {
      name, color, popover, onClick, disabled, ...restRightActionProps
    } = rightAction || {};

    const {
      links, children, position, ...restPopoverProps
    } = popover || {};

    if (disabled || (isEmpty && (!showMenuOnLoad || (showMenuOnLoad && !isLoading)))) return null;

    return (
      <RightCTAWrapper>
        {(links && links?.length && !onClick) ? (
          <Popover
            {...restPopoverProps}
            position={position || 'bottomRight'}
            links={links}
            trigger={(
              <StyledLink variant="inline">
                <Icon
                  {...restRightActionProps}
                  veryTiny
                  name={name || 'more'}
                  color={color || Colors.Gray}
                />
              </StyledLink>
            )}
          >
            {children}
          </Popover>
        ) : (
          <StyledLink variant="inline" onClick={onClick}>
            <Icon
              {...restRightActionProps}
              veryTiny
              name={name || 'more'}
              color={color || Colors.Gray}
            />
          </StyledLink>

        )}

      </RightCTAWrapper>
    );
  };

  return (
    <StyledCardBase
      width={CARD_WIDTH}
      height={CARD_HEIGHT}
      isMobile={isMobile || isFixedSmall}
      {...rest}
    >
      {_renderImagePreview()}
      {_renderTextSection()}
      {_renderRightCTA()}
    </StyledCardBase>
  );
};

export default (withAppManager(CardVideoPreview): React$ComponentType<ManagedProps<Props>>);
